import { DEFAULT_INDEX_GROUP } from '@/constants/constants';
import type { OtherGroupIndexEntry } from '@/features/Markets/api/otherGroupIndex.api';
import type {
  GroupIndexDataRecord,
  GroupIndexSymbol
} from '@/pages/Markets/MarketsComponents/shared/useGroupIndexQueries';
import {
  calculateDelta,
  priceValue,
  symbolKeyString,
  useEnrichedSections
} from '@/pages/Markets/MarketsComponents/shared/useGroupIndexQueries';

import type { MultiGroupTableRowType, MultiGroupTableSectionType } from './types';

type RowType = MultiGroupTableSectionType['rows'][number];

const getRowSymbols = (row: RowType): GroupIndexSymbol[] => [
  row.symbolA,
  { ...row.symbolB, from: '1v' }
];

function enrichSymbolA(data: OtherGroupIndexEntry[]): Partial<MultiGroupTableRowType> {
  if (data.length === 0) return {};
  const first = data[0];
  const firstPrice = priceValue(first.values);
  const secondPrice = priceValue(data[1]?.values);
  const patch: Partial<MultiGroupTableRowType> = {
    date: first.transactionTime,
    last: firstPrice,
    periodAbs: first.values.PeriodAbs,
    symbolAMetadata: first.metadata
  };
  if (firstPrice && secondPrice) {
    patch.delta = calculateDelta(firstPrice, secondPrice);
  }
  return patch;
}

function enrichSymbolB(data: OtherGroupIndexEntry[]): Partial<MultiGroupTableRowType> {
  if (data.length === 0) return {};
  const first = data[0];
  return {
    bValues: first.values,
    symbolBMetadata: first.metadata
  };
}

function enrichRow(row: RowType, dataRecord: GroupIndexDataRecord): RowType {
  let enriched = row;
  if (row.symbolA.group !== DEFAULT_INDEX_GROUP && row.symbolA.key.Code) {
    const data = dataRecord[symbolKeyString(row.symbolA.key)];
    if (data) enriched = { ...enriched, ...enrichSymbolA(data) };
  }
  if (row.symbolB.group !== DEFAULT_INDEX_GROUP && row.symbolB.key.Code) {
    const data = dataRecord[symbolKeyString(row.symbolB.key)];
    if (data) enriched = { ...enriched, ...enrichSymbolB(data) };
  }
  return enriched;
}

export function useMultiGroupTableData(sections: MultiGroupTableSectionType[] | undefined): {
  sections: MultiGroupTableSectionType[] | undefined;
  isLoading: boolean;
} {
  return useEnrichedSections(sections, getRowSymbols, enrichRow);
}
